import { useMemo } from 'react';
import { Button } from '@ai-tutor/ui';
import { Loader2, Pencil } from 'lucide-react';
import { notify } from '@/lib/notify';
import {
  useRegenerateLesson,
  useDiscardStaged,
} from '@/hooks/lesson/useSources';
import { useVersionList } from '@/hooks/lesson/useVersions';
import {
  bumpMajor,
  computeStagedDiff,
  findDuplicateVersion,
} from '@/lib/sources/stagedDiff';
import { PastVersionNotice } from './PastVersionNotice';
import type { LessonMetadata } from '@/lib/api-client';

interface StagedActionBarProps {
  /**
   * Called once a new version has been requested. The page's sources sheet
   * (below `lg`) closes on it, so the rebuild that starts in the lesson is
   * on screen rather than hidden behind the sheet.
   */
  onCreated?: () => void;
  lessonId: string;
  lesson?: LessonMetadata;
  /**
   * Read-only mode - set when the user is viewing an older (non-latest)
   * version. Replaces both idle and dirty states with a notice telling
   * the user to switch back to the latest to make changes.
   */
  isLocked?: boolean;
  /**
   * Why `isLocked` is set. A past version and a version still building are
   * both read-only, but only one of them is fixed by switching: telling the
   * user on a building v2 to "switch to v2" pointed them at where they
   * already were. Defaults to `past-version`, the original meaning.
   */
  lockReason?: 'past-version' | 'building';
  /**
   * The latest (highest-numbered) version, used in the locked-mode notice.
   * Optional because it may be undefined while the version list is loading.
   */
  latestVersion?: string;
  /**
   * Source ids currently active for this lesson (not soft-deleted). Used to
   * filter out already-deleted sources from the "removed" diff. A
   * soft-delete persists in the current version's `source_ids` snapshot
   * (versions are immutable), but the user has already acted on it - surfacing
   * it again as "1 removed" alongside genuine edits is misleading. Defaults
   * to "treat every id as active" so callers that don't pass this still get
   * the original behavior.
   */
  activeSourceIds?: Set<string>;
  /**
   * Count of active sources still ingesting or awaiting chapter selection.
   * While > 0, creating a version is blocked: a snapshot now would exclude the
   * in-flight source and strand it as a permanent staged diff (the bug where a
   * big PDF added just before "create version" never made it into the version).
   * Discard stays available so the user can still back out.
   */
  sourcesNotReadyCount?: number;
}

/**
 * Always-visible bottom bar that consumes the staged source selection +
 * draft prompt. Diff is computed client-side from lesson metadata vs the
 * current LessonVersion's source_ids - no separate /pending-sources call.
 */
export function StagedActionBar({
  lessonId,
  lesson,
  isLocked = false,
  lockReason = 'past-version',
  latestVersion,
  activeSourceIds,
  sourcesNotReadyCount = 0,
  onCreated,
}: StagedActionBarProps) {
  const versionList = useVersionList(lessonId);
  const regenerate = useRegenerateLesson(lessonId);
  const discard = useDiscardStaged(lessonId);

  const currentVersion = lesson?.current_version ?? '1.0';
  const currentVersionRecord = versionList.data?.versions.find(
    v => v.version === currentVersion
  );

  // The diff itself is `lib/sources/stagedDiff` — pure, and unit-tested there
  // across the soft-deleted / synthetic / null-vs-empty cases that make the
  // three numbers on screen right for the right reasons.
  const { addedCount, removedCount, promptChanged, hasDiff } = useMemo(
    () =>
      computeStagedDiff({
        stagedSourceIds: lesson?.staged_source_ids,
        currentVersionSourceIds: currentVersionRecord?.source_ids,
        hasStagedPrompt: Boolean(lesson?.staged_prompt),
        activeSourceIds,
        currentVersionKnown: Boolean(currentVersionRecord),
      }),
    [
      lesson?.staged_source_ids,
      lesson?.staged_prompt,
      currentVersionRecord,
      activeSourceIds,
    ]
  );

  const nextVersion = bumpMajor(currentVersion);
  const isMutating = regenerate.isPending || discard.isPending;

  // Detect if the staged state matches any prior non-current version. If so,
  // creating a new version would just resurrect an existing snapshot - block
  // it and show the user which version it matches. Versions whose user_prompt
  // is null are legacy snapshots without a captured prompt; we can't compare
  // those confidently, so we skip them (server enforces the same rule).
  const duplicateOf = useMemo(
    () =>
      findDuplicateVersion({
        hasDiff,
        versions: versionList.data?.versions,
        stagedSourceIds: lesson?.staged_source_ids,
        currentVersionSourceIds: currentVersionRecord?.source_ids,
        stagedPrompt: lesson?.staged_prompt,
        userPrompt: lesson?.user_prompt,
      }),
    [
      hasDiff,
      versionList.data,
      lesson?.staged_source_ids,
      lesson?.staged_prompt,
      lesson?.user_prompt,
      currentVersionRecord?.source_ids,
    ]
  );

  const sourcesNotReady = sourcesNotReadyCount > 0;
  const buttonDisabled =
    !hasDiff || isMutating || Boolean(duplicateOf) || sourcesNotReady;

  const summaryParts: string[] = [];
  if (addedCount > 0) summaryParts.push(`${addedCount} added`);
  if (removedCount > 0) summaryParts.push(`${removedCount} unchecked`);
  if (promptChanged) summaryParts.push('prompt edited');

  const handleCreate = async () => {
    try {
      const res = await regenerate.mutateAsync();
      notify.success(`Creating v${res.new_version}…`);
      onCreated?.();
    } catch (err: any) {
      notify.error(err, "Couldn't create version");
    }
  };

  const handleDiscard = async () => {
    try {
      await discard.mutateAsync();
      notify.success('Staged changes discarded');
    } catch (err: any) {
      notify.error(err, "Couldn't discard staged changes");
    }
  };

  // Locked: user is viewing an older (non-latest) version. Editing isn't
  // allowed here - staging only happens against the latest version. Show a
  // notice and short-circuit both idle and dirty render paths.
  if (isLocked && lockReason === 'building') {
    return (
      <div className='border-t border-border/60 bg-transparent px-4 py-2.5'>
        <p className='text-xs text-muted-foreground'>
          <span className='font-mono font-medium text-foreground'>
            v{currentVersion}
          </span>{' '}
          is being built. You can make changes once it&apos;s ready.
        </p>
      </div>
    );
  }
  if (isLocked) {
    return (
      <PastVersionNotice
        currentVersion={currentVersion}
        latestVersion={latestVersion}
        variant='bar'
      />
    );
  }

  // Idle: quiet hint, no buttons. Keeps the affordance discoverable without
  // shouting "Create v2.0" at users who haven't made any changes yet.
  // bg-transparent so the SourcesPanel's translucent surface shows through.
  if (!hasDiff) {
    return (
      <div className='border-t border-border/60 bg-transparent px-4 py-2.5'>
        <p className='text-xs text-muted-foreground'>
          Check sources or edit the prompt to draft{' '}
          <span className='font-mono font-medium text-foreground'>
            v{nextVersion}
          </span>
        </p>
      </div>
    );
  }

  // Dirty: two-row drawer - diff chips on top, subline + actions below.
  // bg-transparent matches the SourcesPanel; the top border + soft shadow
  // delineate the action area without breaking the panel's surface tone.
  return (
    <div className='border-t border-border/60 bg-transparent px-4 py-3 shadow-[0_-4px_12px_-4px_rgb(0_0_0_/_0.04)] duration-200 animate-in fade-in slide-in-from-bottom-2 dark:shadow-[0_-4px_12px_-4px_rgb(0_0_0_/_0.3)]'>
      <div className='mb-2.5 flex flex-wrap items-center gap-1.5'>
        {addedCount > 0 && (
          <span className='inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'>
            {addedCount} added
          </span>
        )}
        {removedCount > 0 && (
          <span className='inline-flex items-center gap-1 rounded-md bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'>
            {removedCount} removed
          </span>
        )}
        {promptChanged && (
          <span className='inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground'>
            <Pencil className='h-3 w-3' strokeWidth={2.5} /> Prompt edited
          </span>
        )}
      </div>

      <div className='flex items-center justify-between gap-2'>
        {/* `text-warning-emphasis`, not `text-amber-600`: this subline is the
            ONLY thing explaining why Apply is inert, and amber-600 measured
            2.85:1 on the panel surface at 12px — well under the 4.5 text gate.
            The emphasis token is 4.57:1 here and 11.65:1 in dark, and is the
            same swap already made for success and error copy on tints. Caught
            by `App/MyLearning/Desktop/Behaviour/Lesson/Sources/BlocksAPointlessVersion`,
            which is the first story to render either blocked state. */}
        <p className='truncate text-xs text-muted-foreground'>
          {sourcesNotReady ? (
            <span className='text-warning-emphasis'>
              {sourcesNotReadyCount === 1
                ? '1 source is still processing.'
                : `${sourcesNotReadyCount} sources are still processing.`}{' '}
              Finish before saving a version.
            </span>
          ) : duplicateOf ? (
            <span className='text-warning-emphasis'>
              These changes match{' '}
              <span className='font-mono font-medium'>
                v{duplicateOf.version}
              </span>{' '}
              - nothing new to save
            </span>
          ) : (
            <>
              Next version:{' '}
              <span className='font-mono font-medium text-foreground'>
                v{nextVersion}
              </span>
            </>
          )}
        </p>
        {/* 32px buttons, 44px tap areas on a phone; the 6px gap is wider
            than the (44 - 32) / 2 overhang, so the two never overlap. */}
        <div className='[&>button]:tap-target flex flex-shrink-0 items-center gap-1.5'>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleDiscard}
            disabled={isMutating}
            className='h-8 text-xs text-muted-foreground hover:bg-muted hover:text-foreground'
          >
            Discard
          </Button>
          <Button
            size='sm'
            onClick={handleCreate}
            disabled={buttonDisabled}
            className='h-8 text-xs'
          >
            {regenerate.isPending ? (
              <>
                <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
                Applying…
              </>
            ) : (
              'Apply changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
